import type { BlogPost, BlogListResponse } from "@/lib/data/types";

const API_BASE = process.env.API_URL || "https://internal.electrifiedmarina.com";
const IMAGE_BASE = process.env.NEXT_PUBLIC_API_URL || "https://internal.electrifiedmarina.com";

interface FetchBlogParams {
  category?: string;
  page?: number;
  per_page?: number;
}

/**
 * Fetch published blog posts from the backend API.
 */
export async function fetchBlogPosts(
  params: FetchBlogParams = {}
): Promise<BlogListResponse> {
  const url = new URL(`${API_BASE}/api/public/blog`);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== "all") {
      url.searchParams.set(key, String(value));
    }
  });

  const res = await fetch(url.toString(), {
    next: { revalidate: 300, tags: ["blog"] },
  });

  if (!res.ok) {
    console.error(`Blog API error: ${res.status} ${res.statusText}`);
    return { data: [], total: 0, page: 1, per_page: 12 };
  }

  return res.json();
}

/**
 * Fetch a single published blog post by slug.
 */
export async function fetchBlogPost(
  slug: string
): Promise<BlogPost | null> {
  const res = await fetch(`${API_BASE}/api/public/blog/${encodeURIComponent(slug)}`, {
    next: { revalidate: 300, tags: ["blog", `blog-post-${slug}`] },
  });

  if (!res.ok) return null;
  return res.json();
}

/** Get full image URL for blog images */
export function blogImageUrl(path: string | null): string | null {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) {
    return path;
  }
  return `${IMAGE_BASE}${path}`;
}

/** Resolve backend-hosted image URLs in saved blog HTML without mutating the saved HTML. */
export function normalizeBlogBodyHtml(html: string | null | undefined): string {
  if (!html) return "";

  return html.replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(["'])(\/storage\/[^"']+)\2/gi,
    (_match, prefix: string, quote: string, path: string) => {
      return `${prefix}${quote}${blogImageUrl(path) ?? path}${quote}`;
    }
  );
}

/** Format category slug to display label */
export function categoryLabel(category: string): string {
  const labels: Record<string, string> = {
    "buyers-guide": "Buyer's Guide",
    "news": "News",
    "myth-vs-fact": "Myth vs Fact",
    "boat-of-the-month": "Boat of the Month",
    "tips": "Tips",
    "events": "Events",
  };
  return labels[category] || category;
}

/** Format ISO date string to readable format */
export function formatBlogDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
